import { toDateKey } from "../../scheduler";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Weekday of a `YYYY-MM-DD` key, Monday = 0, as the backend counts them. */
function weekdayOf(dateKey: string): number {
  return (new Date(`${dateKey}T12:00:00Z`).getUTCDay() + 6) % 7;
}

/**
 * The next days the therapist works, as `YYYY-MM-DD` keys in their timezone.
 *
 * Offered as the days to pick from, so a patient never lands on a day with
 * nothing to book. The portal is told the weekdays, not the hours, so today
 * counts as a working day even if its last slot has gone: the empty state
 * then offers the next one.
 */
export function upcomingWorkingDays(
  from: Date,
  activeWeekdays: readonly number[],
  timezone: string,
  count = 8,
  horizonDays = 42
): string[] {
  const days: string[] = [];
  for (let offset = 0; offset < horizonDays && days.length < count; offset += 1) {
    const key = toDateKey(new Date(from.getTime() + offset * DAY_MS), timezone);
    if (activeWeekdays.includes(weekdayOf(key)) && !days.includes(key)) days.push(key);
  }
  return days;
}

/** "mié, 9 sept" — a day key read in the patient's language. */
export function formatDayChip(dateKey: string, language: string): string {
  return new Intl.DateTimeFormat(language, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${dateKey}T12:00:00Z`));
}

/** "miércoles, 9 de septiembre" — the same day spelled out for a sentence. */
export function formatDayLong(dateKey: string, language: string): string {
  return new Intl.DateTimeFormat(language, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(`${dateKey}T12:00:00Z`));
}
